import type {EntityCustomFieldType} from 'nutshell-graphql-types';
import type {
    EntityAttribute,
    EntityAttributeValue,
    CustomFieldValueInput,
} from 'nutshell-graphql-types';

import {EnvelopeNewIcon} from '../../icon/svg/envelope-new-icon';
import {EraserIcon} from '../../icon/svg/eraser-icon';
import {ExternalArrowRegularIcon} from '../../icon/svg/external-arrow-regular-icon';
import {PencilOutlineIcon} from '../../icon/svg/pencil-outline-icon';
import {PhoneOutlineIcon} from '../../icon/svg/phone-outline-icon';
import type {ToolbarOption} from '../../toolbar/toolbar';

import type {RowType} from '../types';

import type {ClickToCallPropsType} from './custom-field-row';

type CustomFieldTextValue = {
    type: 'text';
    value: string | null | undefined;
};

type CustomFieldDateValue = {
    type: 'date';
    value: number | null | undefined;
};

export type CustomFieldEnumOption = {
    value: string;
    label: string;
};

type CustomFieldEnumValue = {
    type: 'enum';
    allowMultiple: boolean;
    selected: string[] | null | undefined;
    options: CustomFieldEnumOption[];
};

type CustomFieldPhoneValue = {
    type: 'phone';
    numberFormattedWithCountryCode: string | null | undefined;
    extension: string | null | undefined;
};

type CustomFieldCurrencyValue = {
    type: 'currency';
    value:
        | {
              currency: string;
              amount: number;
          }
        | null
        | undefined;
    currencyOptions: string[];
    defaultCurrency: string;
};

export type CustomFieldAddressValue = {
    type: 'address';
    value:
        | {
              address1: string;
              address2: string;
              address3: string;
              city: string;
              state: string;
              postalCode: string;
              country: string;
              fullAddress: string | null | undefined;
          }
        | null
        | undefined;
};

type CustomFieldValue =
    | CustomFieldTextValue
    | CustomFieldDateValue
    | CustomFieldEnumValue
    | CustomFieldCurrencyValue
    | CustomFieldAddressValue
    | CustomFieldPhoneValue;

export function getNormalizedCustomField(
    field: EntityAttribute,
    value?: EntityAttributeValue | null
): CustomFieldValue {
    const currencyOptions =
        field.__typename === 'EntityAttributeCurrency' ? field.currencyOptions : [];
    const defaultCurrency =
        field.__typename === 'EntityAttributeCurrency' ? field.defaultCurrency : 'USD';

    switch (field.attribute.type) {
        case 'LOCATION':
            if (value && value.__typename === 'EntityAttributeValueLocation') {
                return {
                    type: 'address',
                    value: {
                        address1: value.address_1 || '',
                        address2: value.address_2 || '',
                        address3: value.address_3 || '',
                        city: value.city || '',
                        state: value.state || '',
                        postalCode: value.postalCode || '',
                        country: value.country || '',
                        fullAddress: value.fullAddressAllFields,
                    },
                };
            } else {
                return {
                    type: 'address',
                    value: undefined,
                };
            }
        case 'TEXT':
        case 'STRING':
            if (
                value &&
                (value.__typename === 'EntityAttributeValueText' ||
                    value.__typename === 'EntityAttributeValueString')
            ) {
                return {
                    type: 'text',
                    value: value.value,
                };
            } else {
                return {
                    type: 'text',
                    value: undefined,
                };
            }
        case 'URL':
            if (value && value.__typename === 'EntityAttributeValueUrl') {
                return {
                    type: 'text',
                    value: value.display,
                };
            } else {
                return {
                    type: 'text',
                    value: undefined,
                };
            }
        case 'EMAIL':
            if (value && value.__typename === 'EntityAttributeValueEmail') {
                return {
                    type: 'text',
                    value: value.display,
                };
            } else {
                return {
                    type: 'text',
                    value: undefined,
                };
            }
        case 'PHONE':
            if (value && value.__typename === 'EntityAttributeValuePhone') {
                return {
                    type: 'phone',
                    extension: value.extension,
                    numberFormattedWithCountryCode: value.numberFormattedWithCountryCode,
                };
            } else {
                return {
                    type: 'phone',
                    extension: undefined,
                    numberFormattedWithCountryCode: undefined,
                };
            }
        case 'DATE':
            if (value && value.__typename === 'EntityAttributeValueDate') {
                return {
                    type: 'date',
                    value: value.date,
                };
            } else {
                return {
                    type: 'date',
                    value: undefined,
                };
            }
        case 'ENUM':
            if (field.__typename === 'EntityAttributeEnum') {
                return {
                    type: 'enum',
                    allowMultiple: field.allowMultiple,
                    selected: getCustomFieldEnumValue(value),
                    options: field.options.map((option) => ({
                        value: option.id,
                        label: option.name,
                    })),
                };
            } else {
                return {
                    type: 'enum',
                    allowMultiple: false,
                    selected: undefined,
                    options: [],
                };
            }
        case 'CURRENCY':
            if (value && value.__typename === 'EntityAttributeValueCurrency') {
                return {
                    type: 'currency',
                    value: {
                        currency: value.currencyValue.currency,
                        amount: value.currencyValue.amount,
                    },
                    currencyOptions,
                    defaultCurrency,
                };
            } else {
                return {
                    type: 'currency',
                    value: undefined,
                    currencyOptions,
                    defaultCurrency,
                };
            }
        default:
            return {
                type: 'text',
                value: undefined,
            };
    }
}

export function getIsCustomFieldMultiLine(field: EntityAttribute): boolean {
    switch (field.attribute.type) {
        case 'URL':
        case 'EMAIL':
        case 'PHONE':
        case 'STRING':
            return false;
        default:
            return true;
    }
}

const getCustomFieldEnumValue = (
    value?: EntityAttributeValue | null
): string[] | null | undefined => {
    if (value && value.__typename === 'EntityAttributeValueEnum') {
        return [value.value];
    } else if (value && value.__typename === 'EntityAttributeValueset') {
        // @ts-expect-error
        return value.all.flatMap((v) =>
            v.__typename === 'EntityAttributeValueEnum' ? [v.value] : []
        );
    } else {
        return undefined;
    }
};

// Some custom field types are note clearable, see previous behavior
// https://github.com/nutshellcrm/nutshell/blob/38947923ce60e55ac4c98096ba8b0a6c196bbc98/public/include/less/_eip2.less#L371-L376
export function getIsCustomFieldClearable(
    field: EntityAttribute,
    value?: EntityAttributeValue | null
): boolean {
    if (!value) {
        return false;
    }

    switch (field.attribute.type) {
        case 'TEXT':
        case 'STRING':
        case 'CURRENCY':
        case 'ENUM':
            return false;
        default:
            return true;
    }
}

export function getCustomFieldAction(
    field: EntityAttribute,
    value?: EntityAttributeValue | null,
    phoneCallProps?: ClickToCallPropsType | null,
    setClickToCallPopoverOpen?: ((arg1: boolean) => unknown) | null
): ToolbarOption | null | undefined {
    switch (field.attribute.type) {
        case 'EMAIL':
            if (value && value.__typename === 'EntityAttributeValueEmail') {
                return {
                    id: 'email',
                    icon: EnvelopeNewIcon,
                    tooltipText: 'Send email',
                    onClick: () => {
                        window.open(`mailto:${value.address}`, '_blank');
                    },
                };
            }
            break;
        case 'URL':
            if (value && value.__typename === 'EntityAttributeValueUrl') {
                return {
                    id: 'url',
                    icon: ExternalArrowRegularIcon,
                    tooltipText: 'Open in a new tab',
                    onClick: () => {
                        window.open(value.value, '_blank');
                    },
                };
            }
            break;
        case 'PHONE':
            if (value && value.__typename === 'EntityAttributeValuePhone') {
                const shouldShowCallButton =
                    value.numberFormatted &&
                    phoneCallProps &&
                    phoneCallProps.phoneCallerType &&
                    phoneCallProps.phoneCallerType !== 'kixie';

                if (!shouldShowCallButton || !phoneCallProps || !phoneCallProps.phoneCallerType) {
                    return undefined;
                }

                return {
                    id: 'call',
                    icon: PhoneOutlineIcon,
                    tooltipText: 'Call',
                    domId: phoneCallProps.anchorElementId,
                    onClick: () => {
                        if (
                            phoneCallProps.phoneCallerType &&
                            phoneCallProps.anchorElementId &&
                            value.numberFormatted &&
                            value.E164
                        ) {
                            phoneCallProps.handleCall(
                                value.numberFormatted,
                                value.E164,
                                phoneCallProps.phoneCallerType,
                                phoneCallProps.entityId,
                                phoneCallProps.anchorElementId
                            );

                            if (setClickToCallPopoverOpen != null) {
                                setClickToCallPopoverOpen(true);
                            }
                        }
                    },
                };
            }
            break;
        default:
            return null;
    }
}

export function getCustomFieldValueForMutationFromString(
    type: EntityCustomFieldType,
    newValue?: string | null
) {
    // Our mutations expect null instead of an empty string
    const inputValue = newValue === '' ? null : newValue;

    // Assume we're a string, and override if we're not
    let value: CustomFieldValueInput = {
        string: inputValue,
    };

    switch (type) {
        case 'TEXT':
            value = {
                text: inputValue,
            };
            break;
        case 'ENUM':
            value = {
                enumSingle: inputValue,
            };
            break;
        case 'DATE':
            value = {
                date: inputValue,
            };
            break;
        case 'EMAIL':
            value = {
                email: {
                    address: inputValue || '',
                    display: inputValue || '',
                },
            };
            break;
        case 'LOCATION':
            // We are able to pass the full address string into the address_1
            // field and the backend will know how to parse it properly into the
            // individual pieces
            value = inputValue
                ? {
                      location: {
                          address_1: inputValue,
                      },
                  }
                : {
                      location: null,
                  };
            break;
    }

    return value;
}

export function getCustomFieldRowType(field: EntityAttribute): RowType {
    switch (field.attribute.type) {
        case 'DATE':
            return 'custom-field-date';
        case 'ENUM':
            // @ts-expect-error
            return field.allowMultiple ? 'custom-field-enum-multi' : 'custom-field-enum-single';
        case 'CURRENCY':
            return 'custom-field-currency';
        case 'ADDRESS':
        case 'LOCATION':
            return 'custom-field-address';
        case 'PHONE':
            return 'custom-field-phone';
        case 'EMAIL':
            return 'custom-field-email';
        case 'URL':
            return 'custom-field-url';
        case 'TEXT':
            return 'custom-field-long-text';
        case 'STRING':
        default:
            return 'custom-field-text';
    }
}

export function getToolbarOptions(
    field: EntityAttribute,
    value: EntityAttributeValue | null | undefined,
    phoneCallProps: ClickToCallPropsType | null | undefined,
    setIsEditing: (isEditing: boolean) => void,
    handleClearClicked: () => void,
    getClearButtonRef: (ref?: HTMLElement | null | undefined) => void,
    setClickToCallPopoverOpen?: ((arg1: boolean) => unknown) | null
): ToolbarOption[] {
    const toolbarOptions: ToolbarOption[] = [
        {
            id: 'edit',
            tooltipText: `Edit ${field.attribute.name || 'field'}`,
            onClick: () => {
                setIsEditing(true);
            },
            icon: PencilOutlineIcon,
        },
    ];

    if (getIsCustomFieldClearable(field, value)) {
        toolbarOptions.push({
            id: 'clear',
            tooltipText: 'Clear value',
            onClick: handleClearClicked,
            icon: EraserIcon,
            getRef: (ref) => {
                getClearButtonRef(ref);
            },
        });
    }

    const customAction = getCustomFieldAction(
        field,
        value,
        phoneCallProps,
        setClickToCallPopoverOpen
    );
    if (customAction) {
        toolbarOptions.push(customAction);
    }

    return toolbarOptions;
}
